import React, { useEffect, useState } from "react";

import { IconButton } from "@mui/material";
import { DirectionsBoatOutlined as DirectionsBoatOutlinedIcon } from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import classnames from "classnames";
import { useTranslation } from "react-i18next";
import { FiEdit, FiLink2, FiTrash2 } from "react-icons/fi";
import { IoAirplaneOutline } from "react-icons/io5";
import { RiTruckLine } from "react-icons/ri";
import { TiWarningOutline } from "react-icons/ti";

import { Destination } from "../../../../models/destination";
import { Location, DifferentLocation as LocationAddress } from "../../../../models/location";
import { Material } from "../../../../models/material";
import { ItemJob, QuotedItem } from "../../../../models/sales";
import { convertMetricVolume, isFtMetric } from "../../../../util/converts";
import { DESTINATION, MODE, Mode } from "../../../../util/enums";
import { getColorByDestination } from "../../../../util/getColorByDestination";
import { getMeasurement, getWeightMeasurement } from "../../../../util/getMeasurement";
import InputRange from "../../../Form/InputRange";
import EditItemModal from "../modals/EditItemModal";
import EditMaterialModal from "../modals/EditMaterialModal";
import useStyles, { ImageList, ValueOnCustomerView } from "./styles";

interface CustomerListComponentProps {
  destinations?: Destination[];
  locations: LocationAddress[];
  items: QuotedItem[];
  materials?: Material[];
  viewOnly?: boolean;
  onCustomerView?: boolean;
  deleteItem: (index: number) => void;
  deleteMaterial: (index: number) => void;
  updateItem: (item: QuotedItem, itemIndex: number) => void;
  updateMaterial: (material: Material, materialIndex: number) => void;
}

const CustomListComponent = ({
  items,
  materials,
  deleteItem,
  deleteMaterial,
  updateItem,
  updateMaterial,
  destinations,
  locations,
  viewOnly = false,
  onCustomerView = false,
}: CustomerListComponentProps) => {
  const measurement = getMeasurement();
  const measurementWeight = getWeightMeasurement();

  const { t: translate } = useTranslation();

  const classes = useStyles();
  const [openItemModal, setOpenItemModal] = useState<boolean>(false);
  const [openMaterialModal, setOpenMaterialModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<QuotedItem | null>(null);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | null>(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState<number>(-1);
  const [selectedMaterialIndex, setSelectedMaterialIndex] = useState<number>(-1);
  const [itemList, setItemList] = useState<QuotedItem[]>(items);
  const [typingTimeout, setTypingTimeout] = useState<any>(0);

  const sumTotalItems = (item: ItemJob | Material): any => {
    const total = convertMetricVolume(item.volume, measurement?.code, item.quantity);

    return total;
  };

  useEffect(() => {
    setItemList(items);
  }, [items]);

  const onUpdateQuantityItem = (value: number, index: number) => {
    if (typingTimeout) clearTimeout(typingTimeout);
    const data = [...itemList];

    data[index].itemJob.quantity = value;
    setTypingTimeout(
      setTimeout(() => {
        updateItem(data[index], index);
      }, 500)
    );
  };

  const onUpdateVolumeItem = (value: number, index: number) => {
    if (typingTimeout) clearTimeout(typingTimeout);
    const data = [...itemList];

    if (isFtMetric(measurement?.code)) data[index].itemJob.volume = Number(value) / 35.315;
    else data[index].itemJob.volume = value;

    setTypingTimeout(
      setTimeout(() => {
        updateItem(data[index], index);
      }, 500)
    );
  };

  const onUpdateWeightItem = (value: number, index: number) => {
    if (typingTimeout) clearTimeout(typingTimeout);
    const data = [...itemList];

    data[index].itemJob.weight = value;

    setTypingTimeout(
      setTimeout(() => {
        updateItem(data[index], index);
      }, 500)
    );
  };

  const getIconByModeOrDestination = (
    mode?: MODE | string,
    destination?: Destination,
    destinationResidence?: Location
  ) => {
    if (destination) {
      const destinationName = destination?.name;

      switch (destinationName?.toLowerCase()) {
        case DESTINATION.NOT_GOING.toLowerCase():
          return (
            <TiWarningOutline size="20px" className={classnames("MuiSvgIcon-root", classes.icon)} />
          );
        case DESTINATION.AIR.toLowerCase():
          return (
            <IoAirplaneOutline
              size="20px"
              className={classnames("MuiSvgIcon-root", classes.icon)}
            />
          );
        case DESTINATION.SEA.toLowerCase():
          return <DirectionsBoatOutlinedIcon className={classes.icon} />;
        case DESTINATION.ROAD.toLocaleLowerCase():
          return (
            <RiTruckLine size="20px" className={classnames("MuiSvgIcon-root", classes.icon)} />
          );
        default:
          return;
      }
    }
    if (mode) {
      switch (mode?.toLowerCase()) {
        case Mode.Road.toLowerCase():
          return (
            <RiTruckLine size="20px" className={classnames("MuiSvgIcon-root", classes.icon)} />
          );
        case Mode.Air.toLowerCase():
          return (
            <IoAirplaneOutline
              size="20px"
              className={classnames("MuiSvgIcon-root", classes.icon)}
            />
          );
        case Mode.Sea.toLowerCase():
          return <DirectionsBoatOutlinedIcon className={classes.icon} />;
        default:
          return null;
      }
    }

    if (destinationResidence) {
      return <RiTruckLine size="20px" className={classnames("MuiSvgIcon-root", classes.icon)} />;
    }

    return null;
  };

  return (
    <div className="mt-3">
      <div className="itemsList">
        <ImageList hasActionColumn={!onCustomerView}>
          <span className="item listHeaderItem">Item</span>
          <span className="qty listHeaderItem">Qty</span>
          <span className="volume listHeaderItem">Volume</span>
          <span className="weight listHeaderItem">Weight</span>
          <span className="total listHeaderItem">Total</span>
          {!onCustomerView ? <span className="action"></span> : null}
        </ImageList>
        <section data-cy="roomItemList" className={classes.gridListBody}>
          {itemList.map((item: QuotedItem, index: number) => {
            return (
              <ImageList hasActionColumn={!onCustomerView} key={`${index}-itemJob`}>
                <span
                  className="item d-flex align-items-center"
                  style={{
                    color: getColorByDestination(
                      item?.itemJob?.destination,
                      item?.itemJob?.destinationResidence
                    ),
                  }}
                >
                  {getIconByModeOrDestination(
                    item?.itemJob?.mode?.toLowerCase(),
                    item?.itemJob?.destination,
                    item?.itemJob?.destinationResidence
                  )}
                  {item?.itemJob?.description || ""}
                </span>
                <span className="qty d-flex justify-content-center">
                  {onCustomerView ? (
                    <ValueOnCustomerView>{item?.itemJob?.quantity || 1}</ValueOnCustomerView>
                  ) : (
                    <InputRange
                      decimalPlacesMaxLength={0}
                      containerClassName={classes.containerInputRange}
                      value={item?.itemJob?.quantity || 1}
                      onIncrease={(value) => onUpdateQuantityItem(value, index)}
                      onDecrease={(value) => onUpdateQuantityItem(value, index)}
                      change={(value) => onUpdateQuantityItem(value, index)}
                      range={1}
                      onlyIntValues
                      disabled={viewOnly}
                    />
                  )}
                </span>
                <span className="volume d-flex justify-content-center">
                  {onCustomerView ? (
                    <ValueOnCustomerView>
                      {item?.itemJob?.volume || 0}
                      {measurement?.code}
                    </ValueOnCustomerView>
                  ) : (
                    <InputRange
                      containerClassName={classes.containerInputRange}
                      measurementInputRange={measurement}
                      value={item?.itemJob?.volume || 0}
                      onIncrease={(value) => onUpdateVolumeItem(value, index)}
                      onDecrease={(value) => onUpdateVolumeItem(value, index)}
                      change={(value) => {
                        const nValue = Number(value);

                        if (!Number.isNaN(nValue)) onUpdateVolumeItem(nValue, index);
                      }}
                      minValue={0}
                      disabled={viewOnly}
                    />
                  )}
                </span>
                <span className="weight d-flex justify-content-center">
                  {onCustomerView ? (
                    <ValueOnCustomerView>
                      {item?.itemJob?.weight || 0}
                      {measurementWeight?.code || "kg"}
                    </ValueOnCustomerView>
                  ) : (
                    <InputRange
                      containerClassName={classes.containerInputRange}
                      measurementInputRange={{
                        code: measurementWeight?.code || "kg",
                        decimalPlaces: measurementWeight?.decimalPlaces,
                      }}
                      value={item?.itemJob?.weight || 0}
                      onIncrease={(value) => onUpdateWeightItem(value, index)}
                      onDecrease={(value) => onUpdateWeightItem(value, index)}
                      change={(value) => {
                        const nValue = Number(value);

                        if (!Number.isNaN(nValue)) onUpdateWeightItem(nValue, index);
                      }}
                      minValue={0}
                      disabled={viewOnly}
                    />
                  )}
                </span>
                <span className="total">{sumTotalItems(item.itemJob)}</span>
                {!onCustomerView ? (
                  <div className={`action ${classes.bodyListButtons}`}>
                    {!viewOnly && (
                      <>
                        <IconButton
                          onClick={() => {
                            setSelectedItem(item);
                            setSelectedItemIndex(index);
                            setOpenItemModal(true);
                          }}
                        >
                          <FiEdit size="20px" color="#475467" />
                        </IconButton>
                        <IconButton onClick={() => deleteItem(index)}>
                          <FiTrash2 size="20px" color="#F97066" />
                        </IconButton>
                      </>
                    )}
                  </div>
                ) : null}
              </ImageList>
            );
          })}
        </section>
      </div>
      <div className="materialList mt-3">
        {materials && materials?.length > 0 && (
          <ImageList hasActionColumn={!onCustomerView}>
            <span className="listHeaderItem">{translate("material")}</span>
          </ImageList>
        )}
        <section data-cy="roomMaterialList" className={classes.gridListBody}>
          {materials?.map((material: Material, index: number) => (
            <ImageList
              hasActionColumn={!onCustomerView}
              key={`${index}-material`}
              className={classes.gridListMaterial}
            >
              <span
                className="item d-flex align-items-center"
                style={{
                  color: getColorByDestination(
                    material?.destination,
                    material?.destinationResidence
                  ),
                }}
              >
                {getIconByModeOrDestination(
                  material?.mode,
                  material?.destination,
                  material?.destinationResidence
                )}
                {material?.serviceBooking?.name || ""}
                {material?.itemJobId && (
                  <Tooltip
                    placement="top"
                    title={`${translate("linkedTo")} ${material?.itemJob?.description}`}
                  >
                    <div className={classes.containerIconLink}>
                      <FiLink2 className={classes.ml12} color="#0055A4" />
                    </div>
                  </Tooltip>
                )}
              </span>
              <span className="qty d-flex justify-content-center">
                {onCustomerView || material?.itemJobId ? (
                  <ValueOnCustomerView>{Number(material.quantity)}</ValueOnCustomerView>
                ) : (
                  <InputRange
                    decimalPlacesMaxLength={0}
                    containerClassName={classes.containerInputRange}
                    value={Number(material.quantity)}
                    disabled={viewOnly || !!material?.itemJobId}
                    change={(value: number) => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      const data = materials;

                      data[index].quantity = value;
                      setTypingTimeout(
                        setTimeout(() => {
                          updateMaterial(data[index], index);
                        }, 500)
                      );
                    }}
                    range={1}
                    onlyIntValues
                  />
                )}
              </span>
              <span className="volume d-flex justify-content-center">
                {onCustomerView || material?.itemJobId ? (
                  <ValueOnCustomerView>
                    {material?.volume}
                    {measurement?.code}
                  </ValueOnCustomerView>
                ) : (
                  <InputRange
                    containerClassName={classes.containerInputRange}
                    measurementInputRange={measurement}
                    value={material?.volume}
                    disabled={viewOnly || !!material?.itemJobId}
                    change={(value: number) => {
                      if (typingTimeout) clearTimeout(typingTimeout);
                      const data = materials;

                      if (isFtMetric(measurement?.code)) data[index].volume = Number(value) / 35.3146;
                      else data[index].volume = value;
                      setTypingTimeout(
                        setTimeout(() => {
                          updateMaterial(data[index], index);
                        }, 500)
                      );
                    }}
                    minValue={0}
                  />
                )}
              </span>
              <span className="weight d-flex justify-content-center">
                {onCustomerView || material?.itemJobId ? (
                  <ValueOnCustomerView>
                    {material?.weight || 0}
                    {measurementWeight?.code}
                  </ValueOnCustomerView>
                ) : (
                  <InputRange
                    containerClassName={classes.containerInputRange}
                    measurementInputRange={{
                      code: measurementWeight?.code || "kg",
                      decimalPlaces: measurementWeight?.decimalPlaces,
                    }}
                    value={material?.weight || 0}
                    minValue={0}
                    disabled={viewOnly || !!material?.itemJobId}
                    change={(value: number) => {
                      const data = [...materials];

                      data[index].weight = value;
                      updateMaterial(data[index], index);
                    }}
                  />
                )}
              </span>
              <span className="total d-flex justify-content-center">
                {`${sumTotalItems(material)}`}
              </span>
              {viewOnly || material?.itemJobId ? (
                <></>
              ) : (
                <div className={`action ${classes.bodyListButtons}`}>
                  <IconButton
                    onClick={() => {
                      setSelectedMaterial(material);
                      setSelectedMaterialIndex(index);
                      setOpenMaterialModal(true);
                    }}
                  >
                    <FiEdit size="20px" color="#475467" />
                  </IconButton>
                  <IconButton onClick={() => deleteMaterial(index)}>
                    <FiTrash2 size="20px" color="#F97066" />
                  </IconButton>
                </div>
              )}
            </ImageList>
          ))}
        </section>
      </div>
      {openItemModal && (
        <EditItemModal
          oldQuotedItem={selectedItem}
          openModal={openItemModal}
          locations={locations}
          destinations={destinations || []}
          closeModal={() => {
            setOpenItemModal(false);
            setSelectedItem(null);
          }}
          submit={(value: QuotedItem) => {
            const data = items;

            data[selectedItemIndex] = value;
            updateItem(value, selectedItemIndex);
          }}
        />
      )}
      {openMaterialModal && (
        <EditMaterialModal
          material={selectedMaterial}
          openModal={openMaterialModal}
          locations={locations}
          destinations={destinations || []}
          closeModal={() => {
            setOpenMaterialModal(false);
            setSelectedMaterial(null);
          }}
          submit={(value: Material) => {
            if (selectedMaterialIndex !== -1) {
              updateMaterial(value, selectedMaterialIndex);
            }
          }}
        />
      )}
    </div>
  );
};

export default CustomListComponent;
